import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Privacy Policy | Close Now Lending"
        description="Close Now Lending privacy policy. Learn how we collect, use, and protect your personal information when you use our real estate lending services."
        url="/privacy"
      />
      <Header />

      {/* Hero */}
      <section className="bg-slate-900 pt-28 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">Privacy Policy</h1>
            <p className="text-gray-400 text-sm">Last updated: February 1, 2026</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto prose prose-gray prose-lg" data-testid="privacy-content">

            <p className="text-gray-700 leading-relaxed">
              Close Now Lending Corp ("we," "our," or "us") is a financial services company committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our financial services, including real estate investment lending.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">1. Information We Collect</h2>
            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Personal Information</h3>
            <p className="text-gray-700 leading-relaxed">
              We may collect personal information that you voluntarily provide when you fill out a contact form, submit a loan inquiry, apply for a loan, or otherwise communicate with us. This may include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Mailing address</li>
              <li>Social Security Number (for loan applications only)</li>
              <li>Financial information (income, assets, credit history)</li>
              <li>Property information related to loan requests</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Automatically Collected Information</h3>
            <p className="text-gray-700 leading-relaxed">
              When you visit our website, we may automatically collect certain information, including your IP address, browser type, operating system, referring URLs, access times, and pages viewed. This information is used to analyze trends, administer the site, and improve our services.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li>Process and evaluate your loan applications and inquiries</li>
              <li>Communicate with you regarding your account, applications, and services</li>
              <li>Provide, maintain, and improve our website and services</li>
              <li>Send you marketing communications (with your consent)</li>
              <li>Comply with legal obligations and regulatory requirements</li>
              <li>Detect, prevent, and address fraud or other illegal activities</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">3. Information Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed">
              We do not sell your personal information. We may share your information in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li><strong>Service Providers:</strong> With third-party vendors who assist in operating our business (e.g., appraisers, title companies, credit reporting agencies) under strict confidentiality agreements.</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, legal process, or governmental request.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
              <li><strong>Consent:</strong> With your explicit consent or at your direction.</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">4. Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement reasonable administrative, technical, and physical safeguards to protect your personal information from unauthorized access, use, alteration, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">5. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can control cookie preferences through your browser settings. Disabling cookies may limit certain features of our website.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">6. Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party websites you visit.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">7. Phone Number Privacy & Do-Not-Sell Disclosure</h2>
            <p className="text-gray-700 leading-relaxed">
              Close Now Lending Corp collects phone numbers solely for the purpose of communicating with you about your loan inquiry, application status, and related financial services. <strong>We do not sell, rent, lease, or share your phone number with third parties for promotional or marketing purposes.</strong> Data will not be sold or shared with third parties for promotional or marketing purposes. This policy applies to all users regardless of state of residence.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              Your phone number will only be used by Close Now Lending Corp and its direct service providers (such as title companies, appraisers, or other parties essential to processing your loan) and only to the extent necessary to fulfill our financial services to you.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              You may opt out of receiving phone communications at any time by contacting us at <a href="mailto:info@closenowlending.com" className="text-blue-600 hover:underline">info@closenowlending.com</a> or by replying STOP to any SMS message you receive from us.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">8. SMS/Text Messaging Terms and Conditions</h2>
            <p className="text-gray-700 leading-relaxed">
              By providing your phone number and opting in to receive text messages from Close Now Lending Corp, you agree to the following SMS Terms and Conditions:
            </p>
            <ol className="list-decimal pl-6 text-gray-700 space-y-3 mt-3">
              <li>We use SMS to confirm appointments, send reminders, and notify clients of schedule updates or important changes related to our financial services.</li>
              <li>You can cancel the SMS service at any time. Just text "<strong>STOP</strong>". After you send the SMS message "STOP" to us, we will send you an SMS message to confirm that you have been unsubscribed. After this, you will no longer receive SMS messages from us. If you want to join again, just sign up as you did the first time and we will start sending SMS messages to you again.</li>
              <li>If you are experiencing issues with the messaging program, you can reply with the keyword "<strong>HELP</strong>" for more assistance, or you can get help directly at <a href="mailto:info@closenowlending.com" className="text-blue-600 hover:underline">info@closenowlending.com</a>.</li>
              <li>Carriers are not liable for delayed or undelivered messages.</li>
              <li>As always, message and data rates may apply for any messages sent to you from us and to us from you. Message frequency may vary. If you have any questions about your text plan or data plan, contact your wireless provider.</li>
              <li>If you have any questions regarding privacy, please read our privacy policy: <a href="/privacy" className="text-blue-600 hover:underline">https://closenowlending.com/privacy</a></li>
            </ol>
            <p className="text-gray-700 leading-relaxed mt-4">
              By opting in to SMS communications, you acknowledge that you have read, understood, and agree to these SMS Terms and Conditions. For questions regarding our SMS program, contact us at <a href="mailto:info@closenowlending.com" className="text-blue-600 hover:underline">info@closenowlending.com</a>.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">9. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate or incomplete information</li>
              <li>Request deletion of your personal information (subject to legal retention requirements)</li>
              <li>Opt out of marketing communications at any time</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              To exercise these rights, please contact us using the information provided below.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a minor, please contact us immediately.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. We encourage you to review this policy periodically.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 rounded-lg p-6 mt-4 not-prose">
              <p className="font-semibold text-gray-900">Close Now Lending</p>
              <p className="text-gray-600 mt-1">4601 Yale St, Houston, TX 77018</p>
              <p className="text-gray-600">Phone: <a href="tel:+19034762937" className="text-blue-600 hover:underline">903-476-2937</a></p>
              <p className="text-gray-600">Email: <a href="mailto:info@closenowlending.com" className="text-blue-600 hover:underline">info@closenowlending.com</a></p>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
